import React from 'react';
import useTechSession from '../../hooks/useTechSession';
import useLanguage from '../../hooks/useLanguage';
import useAPI from '../../hooks/useAPI';
import { techSafetyCheck } from '../../services/api';

/**
 * Safety dashboard — shows current conditions and any active alerts.
 */
export default function SafetyDashboard() {
  const { session } = useTechSession();
  const { t, isRTL } = useLanguage();

  const { data, loading } = useAPI(
    () => techSafetyCheck(session?.technicianId, session?.language),
    [session?.technicianId]
  );

  if (loading) return <div style={styles.center}>{t.loading}</div>;

  const safeColor = data?.safe_to_work ? '#00b894' : '#d63031';

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{t.safety}</h2>

      {/* Safe/Unsafe banner */}
      <div style={{ ...styles.banner, background: safeColor + '22', borderColor: safeColor }}>
        <span style={{ fontSize: 28 }}>{data?.safe_to_work ? '\u2705' : '\u26D4'}</span>
        <span style={{ fontSize: 16, fontWeight: 700, color: safeColor }}>
          {data?.safe_to_work ? t.safeToWork : t.dangerStop}
        </span>
      </div>

      {/* Current conditions */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>{t.weather}</h3>
        <div style={styles.row}>
          <span style={styles.label}>{t.temperature}</span>
          <span style={styles.val}>
            {data?.temperature != null ? `${data.temperature}\u00B0C` : 'N/A'}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>{t.wind}</span>
          <span style={styles.val}>
            {data?.wind_speed != null ? `${data.wind_speed} km/h` : 'N/A'}
          </span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Hours Worked</span>
          <span style={styles.val}>{data?.hours_worked || 0}h</span>
        </div>
      </div>

      {/* Active alerts */}
      {data?.alerts?.length > 0 && (
        <div style={styles.section}>
          <h3 style={{ ...styles.sectionTitle, color: '#fdcb6e' }}>Active Alerts</h3>
          {data.alerts.map((alert, i) => (
            <div key={i} style={styles.alertItem}>
              <span style={styles.alertIcon}>
                {alert.type === 'heat_extreme' ? '\uD83D\uDD25' :
                 alert.type === 'heat_high' ? '\u2600\uFE0F' :
                 alert.type === 'wind_high' ? '\uD83D\uDCA8' :
                 alert.type === 'break_reminder' ? '\u2615' : '\u26A0\uFE0F'}
              </span>
              <div>
                <div style={{ fontSize: 14, color: '#e6edf3' }}>
                  {alert.type?.replace(/_/g, ' ')}
                </div>
                <div style={{ fontSize: 12, color: '#8b949e', marginTop: 2 }}>
                  {alert.voice}
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 600, marginTop: 4,
                  color: alert.action === 'stop_work' ? '#d63031' : '#fdcb6e',
                }}>
                  Action: {alert.action?.replace(/_/g, ' ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(!data?.alerts || data.alerts.length === 0) && (
        <div style={{ textAlign: 'center', padding: 24, color: '#8b949e' }}>
          No active alerts. Stay hydrated!
        </div>
      )}
    </div>
  );
}

const styles = {
  center: { textAlign: 'center', padding: 32, color: '#8b949e' },
  banner: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 12, padding: '16px', borderRadius: 12, marginBottom: 14,
    border: '2px solid',
  },
  section: {
    background: '#161b22', borderRadius: 12, padding: '14px 16px', marginBottom: 12,
  },
  sectionTitle: { fontSize: 14, fontWeight: 600, margin: '0 0 8px 0', color: '#e6edf3' },
  row: {
    display: 'flex', justifyContent: 'space-between', padding: '6px 0',
    borderBottom: '1px solid #30363d20',
  },
  label: { fontSize: 13, color: '#8b949e' },
  val: { fontSize: 13, color: '#e6edf3', fontWeight: 600 },
  alertItem: {
    display: 'flex', gap: 10, padding: '10px 0',
    borderBottom: '1px solid #30363d',
  },
  alertIcon: { fontSize: 24, flexShrink: 0 },
};
